class InlineHeaderSearch extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('.search-action__details');
    this.input = this.querySelector('.search-action__input');
    this.form = this.querySelector('.search-action__form');
    this.results = this.querySelector('[data-inline-search-results]');
    this.searchTimeout = null;
    this.activeFetch = null;
    this.currentIndex = -1;
    this.isInteractingWithResults = false;
    this.clearResultsInteractionTimeout = null;

    if (this.input && this.results) {
      const resultsId = `inline-search-results-${Math.random().toString(36).slice(2)}`;
      this.results.id = resultsId;
      this.input.setAttribute('aria-controls', resultsId);
      this.input.setAttribute('aria-owns', resultsId);
    }

    this.details?.addEventListener('toggle', this.handleToggle);
    this.details?.addEventListener('focusout', this.handleFocusout);
    this.input?.addEventListener('input', this.handleInput);
    this.input?.addEventListener('keydown', this.handleKeydown);
    this.form?.addEventListener('submit', this.handleSubmit);
    this.results?.addEventListener('pointerdown', this.handleResultsPointerdown, true);
    this.results?.addEventListener('mousedown', this.handleResultsPointerdown, true);
    this.results?.addEventListener('touchstart', this.handleResultsPointerdown, true);
    this.results?.addEventListener('click', this.handleResultsClick);
  }

  disconnectedCallback() {
    window.clearTimeout(this.searchTimeout);
    window.clearTimeout(this.clearResultsInteractionTimeout);
    this.activeFetch?.abort();
    this.details?.removeEventListener('toggle', this.handleToggle);
    this.details?.removeEventListener('focusout', this.handleFocusout);
    this.input?.removeEventListener('input', this.handleInput);
    this.input?.removeEventListener('keydown', this.handleKeydown);
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.results?.removeEventListener('pointerdown', this.handleResultsPointerdown, true);
    this.results?.removeEventListener('mousedown', this.handleResultsPointerdown, true);
    this.results?.removeEventListener('touchstart', this.handleResultsPointerdown, true);
    this.results?.removeEventListener('click', this.handleResultsClick);
  }

  handleToggle = () => {
    if (!this.details?.open) {
      this.closeResults();
      return;
    }

    requestAnimationFrame(() => {
      this.input?.focus();
    });
  };

  handleFocusout = (event) => {
    if (this.isInteractingWithResults) return;

    if (event.relatedTarget instanceof Node && this.details?.contains(event.relatedTarget)) return;

    this.closeResults();
  };

  handleInput = () => {
    const searchTerm = this.input?.value.trim() ?? '';
    this.currentIndex = -1;

    window.clearTimeout(this.searchTimeout);

    if (!searchTerm) {
      this.closeResults();
      return;
    }

    this.searchTimeout = window.setTimeout(() => {
      this.getSearchResults(searchTerm);
    }, 200);
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.input) {
        this.input.value = '';
        this.input.blur();
      }
      this.closeResults();
      if (this.details) {
        this.details.open = false;
      }
      return;
    }

    if (this.results?.hidden || !this.resultItems.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectResult(this.currentIndex < this.resultItems.length - 1 ? this.currentIndex + 1 : 0);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectResult(this.currentIndex > 0 ? this.currentIndex - 1 : this.resultItems.length - 1);
        break;
      case 'Enter':
        if (this.currentIndex >= 0) {
          event.preventDefault();
          this.resultItems[this.currentIndex]?.querySelector('a')?.click();
        }
        break;
    }
  };

  handleSubmit = (event) => {
    if (!this.input?.value.trim()) {
      event.preventDefault();
      this.input?.focus();
    }
  };

  handleResultsPointerdown = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.results?.contains(target)) return;

    this.isInteractingWithResults = true;
    window.clearTimeout(this.clearResultsInteractionTimeout);
  };

  handleResultsClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const quickAddButton = target.closest('[data-predictive-search-quick-add]');
    if (quickAddButton instanceof HTMLButtonElement) {
      this.handleQuickAdd(event, quickAddButton);
      this.clearResultsInteraction();
      return;
    }

    const productCard = target.closest('.predictive-search-results__card--product');
    const productUrl = productCard instanceof HTMLElement ? productCard.dataset.productUrl : '';
    const productLink = this.getProductLink(productUrl, productCard);

    const isProductCardControl =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('input');

    if (productLink && !isProductCardControl) {
      const shouldOpenInNewTab = event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1;

      event.preventDefault();
      event.stopPropagation();
      this.clearResultsInteraction();

      if (shouldOpenInNewTab) {
        window.open(productLink, '_blank');
        return;
      }

      window.location.href = productLink;
      return;
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (isInteractiveElement) {
      this.clearResultsInteraction();
      return;
    }

    this.clearResultsInteraction();
  };

  getProductLink(productUrl, productCard) {
    if (productUrl) {
      return new URL(productUrl, location.origin).href;
    }

    const link = productCard?.querySelector('.resource-card__link[href]');
    return link instanceof HTMLAnchorElement ? link.href : '';
  }

  clearResultsInteraction() {
    window.clearTimeout(this.clearResultsInteractionTimeout);
    this.clearResultsInteractionTimeout = window.setTimeout(() => {
      this.isInteractingWithResults = false;
    }, 0);
  }

  async handleQuickAdd(event, button) {
    event.preventDefault();
    event.stopPropagation();

    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled || typeof Theme === 'undefined' || !Theme.routes?.cart_add_url) return;

    const label = button.querySelector('.predictive-search-results__quick-add-text');
    const addLabel = button.dataset.addLabel || label?.textContent || '';
    const addedLabel = button.dataset.addedLabel || addLabel;
    const errorLabel = button.dataset.errorLabel || addLabel;
    const quantity = Number(button.dataset.quantity) || 1;

    button.disabled = true;
    if (label) label.textContent = addedLabel;

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', quantity.toString());

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    if (cartItemComponentsSectionIds.length) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const data = await response.json();

      if (data.status) {
        document.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              sourceId: variantId,
              data: {
                message: data.message,
                errors: data.errors,
                description: data.description,
              },
            },
          })
        );
        if (label) label.textContent = errorLabel;
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = cartResponse.ok ? await cartResponse.json() : undefined;

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: variantId,
            data: {
              source: 'predictive-search',
              itemCount: cart?.item_count ?? quantity,
              productId: button.dataset.productId,
              variantId,
              sections: data.sections,
            },
          },
        })
      );
    } catch (error) {
      console.error(error);
      if (label) label.textContent = errorLabel;
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        if (label) label.textContent = addLabel;
      }, 1200);
    }
  }

  get resultItems() {
    if (!this.results) return [];

    return Array.from(
      this.results.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card')
    ).filter((item) => item instanceof HTMLElement);
  }

  selectResult(index) {
    this.currentIndex = index;

    this.resultItems.forEach((item, itemIndex) => {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        item.scrollIntoView({ block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    });
  }

  async getSearchResults(searchTerm) {
    if (!this.results || typeof Theme === 'undefined' || !Theme.routes?.predictive_search_url) return;

    this.activeFetch?.abort();
    const controller = new AbortController();
    this.activeFetch = controller;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', 'predictive-search');

    try {
      const response = await fetch(url.toString(), { signal: controller.signal });
      const sectionMarkup = await response.text();

      if (controller.signal.aborted || this.input?.value.trim() !== searchTerm) return;

      const parsedMarkup = new DOMParser().parseFromString(sectionMarkup, 'text/html');
      const resultsMarkup =
        parsedMarkup.querySelector('.predictive-search-dropdown') ??
        parsedMarkup.querySelector('.shopify-section .predictive-search-dropdown') ??
        parsedMarkup.querySelector('[data-search-results]')?.closest('.predictive-search-dropdown');

      if (!resultsMarkup) {
        this.closeResults();
        return;
      }

      resultsMarkup.setAttribute('aria-expanded', 'true');
      this.results.replaceChildren(resultsMarkup);
      this.results.hidden = false;
      this.input?.setAttribute('aria-expanded', 'true');
    } catch (error) {
      if (!controller.signal.aborted) {
        this.closeResults();
      }
    }
  }

  closeResults() {
    window.clearTimeout(this.searchTimeout);
    this.activeFetch?.abort();
    this.currentIndex = -1;

    if (this.results) {
      this.results.hidden = true;
      this.results.replaceChildren();
    }

    this.input?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('search-button')) {
  customElements.define('search-button', InlineHeaderSearch);
}
